// The one rule for filling a blank category on a Shopping item (g81, g82): the
// label of the aisle the item's text classifies into, and nothing when that
// is Other. It runs when an item is added, on the device for the app and on
// the server for an agent, a script or an import, and again when a list's
// "Order by category" goes from off to on. The device and the server compile
// this same code, so a list reads the same offline. It only fills a blank; a
// category someone typed is never touched.

import { CATEGORIES, rankOf } from "./categories.js";
import { classifyGrocery } from "./classify.js";

const OTHER = rankOf("other");

/** The category an item with this text is given, or null when the classifier cannot place it. */
export function categoryFor(text: string): string | null {
  const rank = classifyGrocery(text).rank;
  return rank === OTHER ? null : (CATEGORIES[rank] as { label: string }).label;
}

export interface Fillable {
  readonly id: string;
  readonly text: string;
  /** Absent on a row from before g79, which reads as none. */
  readonly category?: string | null;
}

/** `[id, aisle label]` for each item with no category that the classifier places, in input order. */
export function categoryFills(items: readonly Fillable[]): [id: string, label: string][] {
  // A long list repeats its texts, so each is classified once.
  const labels = new Map<string, string | null>();
  const fills: [string, string][] = [];
  for (const item of items) {
    if (item.category) continue;
    let label = labels.get(item.text);
    if (label === undefined) {
      label = categoryFor(item.text);
      labels.set(item.text, label);
    }
    if (label !== null) fills.push([item.id, label]);
  }
  return fills;
}

/**
 * The category a new item is saved with (g82): one that was given is kept, and
 * a blank one on a Shopping list gets its aisle. A caller that knows the blank
 * was chosen on purpose does not ask.
 */
export function categoryOnAdd(kind: string, text: string, category: string | null | undefined): string | null {
  if (category) return category;
  return kind === "shopping" ? categoryFor(text) : null;
}
